import { useState } from "react"
import { useNavigate } from "react-router"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { toast } from "sonner"
import { useHasPermission } from "./session-context"
import { Select } from "./components/select"

type SponsorTier = "sustainer" | "founder" | "friend"

const TIER_OPTIONS = ["Sustainer", "Founder", "Friend"] as const

const TIER_MAP: Record<string, SponsorTier> = {
  Sustainer: "sustainer",
  Founder: "founder",
  Friend: "friend",
}

function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  )
}

export function AddSponsorPage() {
  const navigate = useNavigate()
  const canMutate = useHasPermission("sponsors_mutate")
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    tier: "Sustainer",
    since: new Date().getFullYear().toString(),
    monthlyCommitment: "",
    internalNotes: "",
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      toast.error("Name is required")
      return
    }
    if (!form.email.trim()) {
      toast.error("Email is required")
      return
    }

    setSaving(true)
    try {
      const res = await client.api.admin.sponsors.$post({
        json: {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || undefined,
          tier: TIER_MAP[form.tier] ?? "sustainer",
          since: Number(form.since) || new Date().getFullYear(),
          monthlyCommitment: form.monthlyCommitment ? Number(form.monthlyCommitment) * 100 : undefined,
          internalNotes: form.internalNotes.trim() || undefined,
        },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Sponsor added successfully")
      if ("data" in body && body.data && typeof body.data === "object" && "id" in body.data) {
        navigate(`/admin/sponsors/${body.data.id}`)
      } else {
        navigate("/admin/sponsors")
      }
    } catch {
      toast.error("Failed to add sponsor")
    } finally {
      setSaving(false)
    }
  }

  if (!canMutate) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">Add Sponsor</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to add sponsors.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <button
          onClick={() => navigate("/admin/sponsors")}
          className="inline-flex w-fit items-center gap-1 text-sm font-medium text-ink/60 hover:text-ink"
        >
          <BackIcon className="h-4 w-4" />
          Back
        </button>

        <div>
          <p className="text-xs uppercase tracking-wider text-ink/50">New Record</p>
          <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">Add a Sponsor</h1>
          <p className="mt-1 text-sm text-ink/60">
            Add a new sponsor to the program. You can match them with children after creating their profile.
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="rounded-[10px] border border-gold-500 bg-white p-6">
        <div className="space-y-6">
          <div className="grid gap-6 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 flex items-center gap-1 text-sm font-medium text-ink">
                Name <span className="text-red-500">*</span>
              </span>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                placeholder="Full name"
              />
            </label>
            <label className="block">
              <span className="mb-1.5 flex items-center gap-1 text-sm font-medium text-ink">
                Email <span className="text-red-500">*</span>
              </span>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                placeholder="sponsor@email.com"
              />
            </label>
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-ink">Phone</span>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                placeholder="+234 XXX XXX XXXX"
              />
            </label>
            <Select
              label="Tier"
              value={form.tier}
              onChange={(value) => setForm({ ...form, tier: value })}
              options={TIER_OPTIONS}
            />
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-ink">Sponsor Since (Year)</span>
              <input
                type="number"
                min="2000"
                max={new Date().getFullYear()}
                value={form.since}
                onChange={(e) => setForm({ ...form, since: e.target.value })}
                className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                placeholder={new Date().getFullYear().toString()}
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-ink">Monthly Commitment (NGN)</span>
              <input
                type="number"
                min="0"
                value={form.monthlyCommitment}
                onChange={(e) => setForm({ ...form, monthlyCommitment: e.target.value })}
                className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                placeholder="50000"
              />
            </label>
          </div>

          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink">Internal Notes</span>
            <textarea
              value={form.internalNotes}
              onChange={(e) => setForm({ ...form, internalNotes: e.target.value })}
              rows={4}
              className="block w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
              placeholder="Any notes about this sponsor..."
            />
          </label>
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate("/admin/sponsors")}
            className="rounded-full border border-ink/20 px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-full bg-forest-950 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-900 disabled:opacity-50"
          >
            {saving ? "Adding..." : "Add Sponsor"}
          </button>
        </div>
      </form>
    </div>
  )
}
